/**
 * 2025 MyDebugger Contributors – MIT License
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MdPushPin, MdOutlinePushPin } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEOMeta from '../components/SEOMeta';
import { pageSEO } from '../config/seo.config';
import {
  Card,
  Grid,
  ResponsiveContainer,
  Button,
  TabGroup,
  Tab,
  TabPanel,
  Tag,
  Badge,
  Text,
  Tooltip
} from '../design-system';
import { TOOL_PANEL_CLASS } from '../design-system/foundations/layout';
import { getTools, getAllCategories, getToolsByCategory, getPopularTools, getNewTools, Tool, ToolCategory, categories as categoryInfo } from '../tools';
import { excludeById, sortByTitle } from '../utils/toolFilters';
import './Home.css';
import { useTranslation } from '../context/TranslationContext';
import { logEvent } from '../lib/analytics';

const Home: React.FC = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('all');
  const [visibleTools, setVisibleTools] = useState<Tool[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
  const [recentTools, setRecentTools] = useState<Tool[]>([]);
  const [isFeaturedExpanded, setIsFeaturedExpanded] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [pinnedTools, setPinnedTools] = useState<Tool[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const hasFiredSearchStartRef = useRef<boolean>(false);

  const handleSetActiveTab = useCallback((tabId: string) => {
    setActiveTab(tabId);
    try {
      logEvent('browse_tab_change', { tab: tabId, page_path: window.location.pathname + window.location.search });
    } catch {}
  }, []);

  const handleViewModeChange = useCallback((mode: 'grid' | 'list') => {
    setViewMode(mode);
    try { logEvent('browse_view_mode', { mode }); } catch {}
  }, []);

  const allTools = useMemo(() => getTools(), []);
  const categories = useMemo(() => getAllCategories(), []);
  const popularTools = useMemo(() => getPopularTools(), []);
  const schemaData = {
    "@context": "https://schema.org",
    "@type": ["WebSite", "SoftwareApplication"],
    name: "MyDebugger - Advanced Debugging Tools",
    description: "Professional debugging platform with 50+ development tools including JWT decoder, QR code generator, code analysis, and performance monitoring utilities",
    url: "https://mydebugger.vercel.app/",
    applicationCategory: "DeveloperApplication",
    operatingSystem: "Web Browser",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock"
    },
    featureList: [
      "JWT Decoder and Debugger",
      "QR Code Generator and Scanner",
      "Code Analysis and Profiler",
      "Performance Monitoring",
      "Error Tracking System",
      "API Testing Tools",
      "Data Visualization",
      "Encoding/Decoding Utilities",
      "Security Analysis",
      "Database Tools"
    ],
    programmingLanguage: ["JavaScript", "TypeScript", "Python", "Go", "Rust"],
    screenshot: "https://mydebugger.vercel.app/screenshot-home.jpg",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "247"
    },
    potentialAction: {
      "@type": "SearchAction",
      target: "https://mydebugger.vercel.app/?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };
  const observerRef = useRef<IntersectionObserver | null>(null);
  const toolsContainerRef = useRef<HTMLDivElement>(null);
  
  // Simulate loading (reduced from 800ms to 300ms for better UX)
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    
    // Get recently used tools from localStorage (preserve order and cap at 5)
    try {
      const recent = localStorage.getItem('recentTools');
      if (recent) {
        const recentIds = (JSON.parse(recent) as string[]).slice(0, 5);
        const recentToolsData = recentIds
          .map(id => allTools.find(t => t.id === id))
          .filter((t): t is Tool => Boolean(t));
        setRecentTools(recentToolsData);
      }
    } catch (e) {
      console.error('Error loading recent tools:', e);
    }
    
    return () => clearTimeout(timer);
  }, [allTools]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('pinnedTools');
      if (stored) {
        const ids = JSON.parse(stored) as string[];
        const pinned = ids
          .map(id => allTools.find(t => t.id === id))
          .filter((t): t is Tool => Boolean(t));
        setPinnedTools(pinned);
      }
    } catch (e) {
      console.error('Error loading pinned tools:', e);
    }
  }, [allTools]);

  // Handle scroll to show scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Removed intersection observer animation code for better UX
  
  // Update visible tools when search term or active tab changes
  useEffect(() => {
    setIsLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      let filtered: Tool[];
      
      if (activeTab === 'all') {
        filtered = searchTerm ? allTools : excludeById(allTools, recentTools);
      } else if (activeTab === 'popular') {
        filtered = popularTools;
      } else if (activeTab === 'new') {
        filtered = getNewTools();
      } else if (activeTab === 'recent') {
        filtered = recentTools;
      } else {
        filtered = getToolsByCategory(activeTab as ToolCategory);
      }
      
      if (searchTerm) {
        const lowerSearch = searchTerm.toLowerCase();
        filtered = filtered.filter(tool =>
          tool.title.toLowerCase().includes(lowerSearch) ||
          tool.description.toLowerCase().includes(lowerSearch) ||
          tool.metadata.keywords.some(keyword => keyword.toLowerCase().includes(lowerSearch))
        );
      }

      filtered = sortByTitle(filtered, sortOrder);
      setVisibleTools(filtered);
      setIsLoading(false);
    }, 300);
  }, [searchTerm, activeTab, sortOrder, allTools, popularTools, recentTools]);
  
  const handleToolClick = useCallback((tool: Tool) => {
    // Save to recent tools in localStorage
    try {
      const recent = localStorage.getItem('recentTools');
      let recentIds: string[] = recent ? JSON.parse(recent) : [];
      
      // Remove if exists and add to front (most recent)
      recentIds = recentIds.filter(id => id !== tool.id);
      recentIds.unshift(tool.id);
      
      // Keep only the last 10
      recentIds = recentIds.slice(0, 10);
      
      localStorage.setItem('recentTools', JSON.stringify(recentIds));
    } catch (e) {
      console.error('Error saving recent tool:', e);
    }

    // Track click in GA4 for funnel insights
    try {
      logEvent('tool_open_click', {
        tool_id: tool.id,
        tool_title: tool.title,
        tool_category: tool.category,
        page_path: window.location.pathname + window.location.search,
        page_title: document.title,
        page_location: window.location.href,
      });
    } catch (_e) {
      // ignore
    }
  }, []);

  const togglePin = useCallback((tool: Tool) => {
    setPinnedTools(prev => {
      const exists = prev.find(t => t.id === tool.id);
      let updated: Tool[];
      if (exists) {
        updated = prev.filter(t => t.id !== tool.id);
      } else {
        updated = [...prev, tool];
      }
      localStorage.setItem('pinnedTools', JSON.stringify(updated.map(t => t.id)));
      return updated;
    });
  }, []);

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDrop = (index: number) => {
    if (dragIndex === null || dragIndex === index) return;
    setPinnedTools(prev => {
      const updated = [...prev];
      const [item] = updated.splice(dragIndex, 1);
      updated.splice(index, 0, item);
      localStorage.setItem('pinnedTools', JSON.stringify(updated.map(t => t.id)));
      return updated;
    });
    setDragIndex(null);
  };
  
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEOMeta seo={pageSEO.home} path="/" />
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="language" content="en" />
        <meta name="application-name" content="MyDebugger" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="MyDebugger" />
        <link rel="manifest" href="/manifest.json" />
        <meta name="theme-color" content="#2563eb" />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>
        <ResponsiveContainer maxWidth="7xl" className="py-6 px-4 sm:px-6">
        <h1 id="hero-heading" className="text-center text-3xl sm:text-4xl font-extrabold gradient-text mb-6">
          {t('home.title', 'Developer tools')}
        </h1>
        <div className="relative max-w-2xl mx-auto mb-8">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder={t('home.search.placeholder', 'Search tools, keywords, functionality...')}
            className="block w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-full leading-6 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition shadow-sm hover:shadow-md"
            value={searchTerm}
            onChange={(e) => {
              const v = e.target.value;
              if (!hasFiredSearchStartRef.current && v.trim().length > 0) {
                hasFiredSearchStartRef.current = true;
                try { logEvent('search_start', { query: v }); } catch {}
              }
              setSearchTerm(v);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                try { logEvent('search_enter', { query: searchTerm }); } catch {}
              }
            }}
            aria-label={t('home.search.aria', 'Search tools')}
          />
          {searchTerm && (
            <button
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => { setSearchTerm(''); hasFiredSearchStartRef.current = false; try { logEvent('search_clear'); } catch {} }}
              aria-label={t('home.search.clearAria', 'Clear search')}
            >
              <svg className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {pinnedTools.length > 0 && (
          <section className="mb-8 mt-6 bg-gray-50 dark:bg-gray-800/50 rounded-md px-4 py-3">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">{t('home.pinned.title', 'Pinned Tools')}</h2>
            <div className="flex flex-wrap gap-3">
              {pinnedTools.map((tool, idx) => (
                <div
                  key={tool.id}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(idx)}
                  className={`flex items-center border border-gray-200 dark:border-gray-700 px-2 py-1 cursor-move ${TOOL_PANEL_CLASS.replace('p-6', 'p-2')}`}
                >
                  <tool.icon className="h-5 w-5 text-primary-600 dark:text-primary-400 mr-2" />
                  <Link to={tool.route} className="mr-2 text-sm text-gray-900 dark:text-white">
                    {t(`tools.${tool.id}.title`, tool.title)}
                  </Link>
                  <button onClick={() => togglePin(tool)} aria-label="Unpin" title="Unpin Tool" className="text-gray-500 hover:text-red-600">
                    <MdPushPin className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
          {/* New Browse Tools section header */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold heading-gradient">{t('home.browse.title', 'Browse Tools')}</h2>
            <div className="bg-white/70 dark:bg-gray-800/60 rounded-xl p-1 shadow-sm flex">
              <button
                className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white dark:bg-gray-700 shadow' : ''}`}
                onClick={() => handleViewModeChange('grid')}
                aria-pressed={viewMode === 'grid'}
                aria-label={t('home.view.grid', 'Grid view')}
              >
                <svg className="w-4 h-4 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white dark:bg-gray-700 shadow' : ''}`}
                onClick={() => handleViewModeChange('list')}
                aria-pressed={viewMode === 'list'}
                aria-label={t('home.view.list', 'List view')}
              >
                <svg className="w-4 h-4 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>

          {/* Unified horizontal chip tabs */}
          <div className="relative">
            <div className="flex items-center gap-2 overflow-x-auto py-2">
              {[
                { id: 'all', label: t('home.tabs.all', 'All Tools') },
                { id: 'popular', label: t('home.tabs.popular', 'Popular') },
                { id: 'new', label: t('home.tabs.new', 'New') },
                { id: 'recent', label: t('home.tabs.recent', 'Recently Used') },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => handleSetActiveTab(tab.id)}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-300 shadow ring-1 ring-primary-300/50'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => handleSetActiveTab(category)}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                    activeTab === category
                      ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-300 shadow ring-1 ring-primary-300/50'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {t(`categories.${category}.name`, category)}
                </button>
              ))}
            </div>
          </div>

          {/* Active filters */}
          {searchTerm && (
            <div className="mt-2 flex items-center bg-primary-50 dark:bg-gray-700 text-primary-700 dark:text-primary-300 py-1 px-3 rounded-full text-sm w-max">
              <span>{t('home.search.active', 'Search')}: {searchTerm}</span>
              <button
                onClick={() => setSearchTerm('')}
                className="ml-2 text-primary-500 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-200"
                aria-label="Clear search"
              >
                <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
        </section>

        
        {/* Recently Used Tools (only show if there are recent tools and not searching) */}
        {!searchTerm && activeTab !== 'recent' && recentTools.length > 0 && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white">{t('home.recent.title', 'Recently Used Tools')}</h2>
              <Button 
                variant="light" 
                size="sm" 
                onClick={() => setActiveTab('recent')}
              >
                {t('home.recent.viewAll', 'View All')}
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {recentTools.map((tool, idx) => (
                <Link
                  key={`recent-${tool.id}`}
                  to={tool.route}
                  onClick={() => handleToolClick(tool)}
                  className="no-underline"
                >
                  <div className={`flex items-center p-2 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow ${TOOL_PANEL_CLASS.replace('p-6', 'p-2')}`}> 
                    <div className="mr-2 p-1.5 bg-primary-100 dark:bg-primary-900 rounded-xl">
                      <tool.icon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">{tool.title}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{tool.category}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Tools Grid */}
        <section className="mb-12" ref={toolsContainerRef}>
          <div className="mb-4 flex justify-between items-center">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {visibleTools.length > 0 ? (
                <p className="mt-4 mb-4 text-sm text-gray-500 pl-1">
                  {t('home.results.showing', 'Showing {count} tool{plural}')
                    .replace('{count}', String(visibleTools.length))
                    .replace('{plural}', visibleTools.length !== 1 ? 's' : '')}
                </p>
              ) : null}
            </div>
            <div className="flex items-center space-x-2">
              <label htmlFor="sortOrder" className="text-sm text-gray-600 dark:text-gray-400">
                {t('home.sort.label', 'Sort by')}
              </label>
              <select
                id="sortOrder"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                className="text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1"
              >
                <option value="asc">{t('home.sort.az', 'A to Z')}</option>
                <option value="desc">{t('home.sort.za', 'Z to A')}</option>
              </select>
              <Button
                variant="light"
                size="sm"
                onClick={() => {setSearchTerm(''); hasFiredSearchStartRef.current = false; handleSetActiveTab('all');}}
                disabled={!searchTerm && activeTab === 'all'}
              >
                {t('home.filters.clear', 'Clear Filters')}
              </Button>
            </div>
          </div>

          {isLoading ? (
            // Skeleton loading state
            <div className={viewMode === 'grid' ? 
              "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6" :
              "space-y-3"
            }>
              {Array.from({ length: 6 }).map((_, i) => (
                viewMode === 'grid' ? (
                  <div key={`skeleton-${i}`} className="rounded-xl overflow-hidden">
                    <div className="h-32 bg-gray-200 dark:bg-gray-700 skeleton"></div>
                    <div className="p-4 bg-white dark:bg-gray-800">
                      <div className="h-5 w-3/4 mb-2 bg-gray-200 dark:bg-gray-700 skeleton rounded"></div>
                      <div className="h-4 w-1/2 mb-4 bg-gray-200 dark:bg-gray-700 skeleton rounded"></div>
                      <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 skeleton rounded"></div>
                    </div>
                  </div>
                ) : (
                  <div key={`skeleton-${i}`} className={`p-3 border border-gray-200 dark:border-gray-700 flex items-center ${TOOL_PANEL_CLASS.replace('p-6', 'p-3')}`}> 
                    <div className="w-10 h-10 rounded mr-4 bg-gray-200 dark:bg-gray-700 skeleton"></div>
                    <div className="flex-grow">
                      <div className="h-5 w-1/2 mb-2 bg-gray-200 dark:bg-gray-700 skeleton rounded"></div>
                      <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 skeleton rounded"></div>
                    </div>
                  </div>
                )
              ))}
            </div>
          ) : visibleTools.length > 0 ? (
            viewMode === 'grid' ? (
              <div className="tool-cards-container">
                {visibleTools.map((tool, idx) => (
                  <Link
                    key={tool.id}
                    to={tool.route}
                    onClick={() => handleToolClick(tool)}
                    className="no-underline tool-card glass-card"
                    aria-labelledby={`tool-title-${tool.id}`}
                  >
                    {/* Header */}
                    <div className="tool-card-header">
                      <div className="tool-icon-container">
                        <tool.icon className="h-6 w-6 text-primary-600 dark:text-primary-400 tool-icon" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 id={`tool-title-${tool.id}`} className="tool-title truncate">
                          {t(`tools.${tool.id}.title`, tool.title)}
                          <span className="tool-inline-badges">
                            {tool.isPopular && <span className="tool-badge popular">{t('badges.popular', 'POPULAR')}</span>}
                            {tool.isBeta && <span className="tool-badge beta">{t('badges.beta', 'BETA')}</span>}
                          </span>
                        </h2>
                        <p className="tool-category truncate">{t(`categories.${tool.category}.name`, tool.category)}</p>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="tool-card-body">
                      <p className="tool-description">{t(`tools.${tool.id}.description`, tool.description)}</p>
                    </div>

                    {/* Footer meta removed per request */}
                  </Link>
                ))}
              </div>
            ) : (
              // List view
              <div className="space-y-2">
                {visibleTools.map((tool, idx) => (
                  <Link
                    key={`list-${tool.id}`}
                    to={tool.route}
                    onClick={() => handleToolClick(tool)}
                    className="no-underline block tool-card"
                  >
                    <div className={`p-3 border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 flex items-center ${TOOL_PANEL_CLASS.replace('p-6', 'p-3')}`}> 
                      <div className="mr-4 p-2 bg-primary-100 dark:bg-primary-900 rounded-xl tool-icon-container">
                        <tool.icon className="h-5 w-5 text-primary-600 dark:text-primary-400 tool-icon" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center">
                          <h3 className="font-medium text-gray-900 dark:text-white mr-2 truncate">
                            {t(`tools.${tool.id}.title`, tool.title)}
                          </h3>
                          <div className="flex-shrink-0 flex space-x-1">
                            {tool.isBeta && <Tag variant="warning" size="sm">{t('badges.beta', 'BETA')}</Tag>}
                            {tool.isPopular && <Tag size="sm">{t('badges.popular', 'POPULAR')}</Tag>}
                            <button
                              onClick={(e) => { e.preventDefault(); togglePin(tool); }}
                              aria-label="Pin tool"
                              title={pinnedTools.find(t => t.id === tool.id) ? 'Unpin Tool' : 'Pin Tool'}
                            >
                              {pinnedTools.find(t => t.id === tool.id) ? (
                                <MdPushPin className="w-4 h-4 text-primary-600" />
                              ) : (
                                <MdOutlinePushPin className="w-4 h-4 text-gray-600" />
                              )}
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {t(`tools.${tool.id}.description`, tool.description)}
                        </p>
                      </div>
                      <div className="ml-4 flex-shrink-0">
                        <span className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">
                          {t(`categories.${tool.category}.name`, tool.category)}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )
          ) : (
            <div className="text-center py-16 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-gray-100">{t('home.empty.title', 'No tools found')}</h3>
              <p className="mt-1 text-gray-600 dark:text-gray-400">
                {t('home.empty.subtitle', 'No tools found matching your search criteria.')}
              </p>
              <div className="mt-6">
                <Button 
                  onClick={() => {setSearchTerm(''); setActiveTab('all');}} 
                  variant="primary"
                >
                  {t('home.empty.reset', 'Reset Filters')}
                </Button>
              </div>
            </div>
          )}
        </section>
          {/* Categories Navigation */}
        <section className="mb-12">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-5">{t('home.category.title', 'Browse by Category')}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
            {categories.map(category => {
              const meta = categoryInfo[category as ToolCategory];
              return (
                <div
                  key={`cat-${category}`}
                  className={`border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer ${TOOL_PANEL_CLASS.replace('p-6', 'p-4')}`}
                  onClick={() => setActiveTab(category)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {meta?.icon && <meta.icon className="w-5 h-5 text-primary-600" />}
                      <h3 className="font-medium text-gray-900 dark:text-white">{category}</h3>
                    </div>
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                     <p className="text-sm text-gray-500 dark:text-gray-400">
                     {t('home.category.count', '{count} tools').replace('{count}', String(getToolsByCategory(category as ToolCategory).length))}
                     </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Footer CTA */}
        <section className="glass-card rounded-2xl p-8 mb-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-primary-700/20 dark:from-primary-900/20 dark:to-gray-900/40 z-0"></div>
          
          <div className="md:flex md:items-center md:justify-between relative z-10">
            <div className="mb-6 md:mb-0 md:pr-12">
              <Badge color="primary" className="mb-2">{t('home.cta.badge', 'OPEN SOURCE')}</Badge>
              <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-4">
                {t('home.cta.title', 'Need a custom tool?')}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mb-6">
                {t('home.cta.desc', "Can't find what you're looking for? MyDebugger is open source and continually evolving. Suggest new features, report bugs, or contribute directly to our GitHub repository.")}
              </p>
              
              <div className="flex flex-wrap gap-6">
                <a
                  href="https://github.com/HydrogenB/mydebugger/issues/new"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition"
                  data-analytics-event="cta_request_feature"
                  data-analytics-label="Home Request Feature"
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                  {t('home.cta.request', 'Request Feature')}
                </a>
                <a
                  href="https://github.com/HydrogenB/mydebugger"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl shadow-sm text-primary-600 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-primary-400 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition"
                  data-analytics-event="cta_github"
                  data-analytics-label="Home GitHub"
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.268 2.75 1.026A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.026 2.747-1.026.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.579.688.481C19.137 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
                  </svg>
                  {t('home.cta.github', 'Visit GitHub')}
                </a>
              </div>
            </div>
            
            <div className="mt-6 md:mt-0 md:flex-shrink-0">
              <div className={TOOL_PANEL_CLASS}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-3 w-3 bg-red-500 rounded-full"></span>
                  <span className="h-3 w-3 bg-yellow-500 rounded-full"></span>
                  <span className="h-3 w-3 bg-green-500 rounded-full"></span>
                </div>
                <div className="font-mono text-sm text-gray-700 dark:text-gray-300 whitespace-pre">
                  <div>$ npm install</div>                    <div>$ npm run dev</div>
                    <div className="text-primary-600 dark:text-primary-400 mt-2">
                      {"> ready in 1.2s"}
                    </div>
                </div>
              </div>
            </div>
          </div>
          
        </section>
      </ResponsiveContainer>
      
      {/* Scroll to top button */}
      <button
        className={`scroll-to-top p-3 bg-primary-600 text-white rounded-full shadow-lg ${showScrollTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default Home;